import { z } from 'zod';

const nullableStringSchema = z.string().nullable();
const unknownRecordSchema = z.record(z.string(), z.unknown());

const customerPortalBrandingSchema = z.object({
  logo_url: nullableStringSchema,
  primary_color: z.string(),
  secondary_color: z.string(),
  accent_color: z.string(),
});

export const customerPortalMeSchema = z.object({
  customer: z.object({
    id: nullableStringSchema,
    email: z.string(),
    name: z.string(),
    avatar_url: nullableStringSchema,
    joined_at: nullableStringSchema,
  }),
  shop: z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    currency: z.string(),
    branding: customerPortalBrandingSchema,
  }),
  // Same-origin consumers may receive the server-side bearer exchange token.
  // The external headless transport always produces null here.
  token: nullableStringSchema,
});

const customerPortalProductImageSnakeSchema = z.object({
  attachment_image_id: z.object({
    cloudflare_image_id: nullableStringSchema,
  }).nullable(),
});

const customerPortalProductImageCamelSchema = z.object({
  attachment_imageId: z.object({
    cloudflareImageId: nullableStringSchema,
  }).nullable(),
});

export const customerPortalDashboardInvoiceSchema = z.object({
  uniqid: z.string(),
  status: z.string(),
  total: z.string(),
  total_display: nullableStringSchema,
  currency: z.string(),
  created_at: z.string(),
  line_items: z.array(z.object({
    product_title: nullableStringSchema,
    quantity: z.number(),
    product: customerPortalProductImageSnakeSchema.nullable(),
  })),
});

export const customerPortalPaginatedInvoiceSchema = z.object({
  uniqid: z.string(),
  status: z.string(),
  total: z.string(),
  totalDisplay: nullableStringSchema,
  currency: z.string(),
  createdAt: z.string(),
  lineItems: z.array(z.object({
    productTitle: nullableStringSchema,
    quantity: z.number(),
    product: customerPortalProductImageCamelSchema.nullable(),
  })),
});

export const customerPortalPaginatedInvoicesSchema = z.object({
  invoices: z.array(customerPortalPaginatedInvoiceSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    has_more: z.boolean(),
  }),
});

/** Buyer-visible review state for one authenticated customer order. */
export const customerPortalOrderReviewSchema = z.object({
  can_review: z.boolean(),
  already_submitted: z.boolean(),
  feedback_uniqid: nullableStringSchema,
  product_title: nullableStringSchema,
  score: z.union([z.literal(1), z.literal(3), z.literal(5)]).nullable(),
  is_automated: z.boolean(),
  message: nullableStringSchema,
  submitted_at: nullableStringSchema,
});

const customerPortalPaymentSchema = z.object({
  gateway: z.string(),
  /** Merchant-configured buyer label frozen when the payment attempt started. */
  displayName: nullableStringSchema,
  /** Concrete coin gateway key (e.g. USDT_TRC20) for crypto payments — the
   *  buyer-facing label; `gateway` may carry an internal rail name. */
  cryptoGateway: nullableStringSchema.optional(),
  blockchain: nullableStringSchema,
  cryptoAddress: nullableStringSchema,
  apmMethod: nullableStringSchema,
});

export const customerPortalInvoiceDetailSchema = z.object({
  id: z.string(),
  uniqid: z.string(),
  status: z.string(),
  statusDetails: nullableStringSchema,
  total: z.string(),
  totalDisplay: nullableStringSchema,
  subtotal: z.string(),
  discount: z.string(),
  discountDisplay: nullableStringSchema,
  tax: z.string(),
  taxPercentage: z.string(),
  currency: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  gatewayUpdatedAt: nullableStringSchema,
  customFields: unknownRecordSchema.nullable(),
  balancePaidAmount: z.string(),
  tipAmountDisplay: z.string(),
  pricingBreakdown: z.object({
    subtotal: z.string(),
    discount: z.string(),
    volume_discount: z.string(),
    discount_label: z.enum(['Coupon', 'Discount']),
    affiliate_code: nullableStringSchema,
    tip: z.string(),
    gateway_fee: z.string(),
    platform_fee: z.string(),
    processing_fee: z.string(),
    already_paid: z.string(),
    total: z.string(),
    amount_due: z.string(),
    currency: z.string(),
  }),
  currentPaymentAttemptId: nullableStringSchema,
  lineItems: z.array(z.object({
    id: z.string(),
    productTitle: z.string(),
    productType: z.string(),
    variantTitle: nullableStringSchema,
    unitPrice: z.string(),
    quantity: z.number(),
    total: z.string(),
    deliveryText: nullableStringSchema,
    deliverySummary: z.object({
      codes: z.array(z.string()),
      links: z.array(z.object({
        label: z.string(),
        value: z.string(),
        href: nullableStringSchema.optional(),
      })),
      notes: z.array(z.string()),
      serials: z.array(z.object({
        value: z.string(),
        is_replacement: z.boolean(),
        is_replaced: z.boolean(),
      })),
    }).nullable(),
    deliveredAt: nullableStringSchema,
    /**
     * Per-line fulfillment state, resolved server-side
     * (docs/architecture/domains/order-fulfillment.md). Independent of the
     * invoice payment status: a COMPLETED order can still hold items the
     * seller delivers later.
     */
    deliveryStatus: z.enum(['PENDING', 'AWAITING_FULFILLMENT', 'DELIVERED', 'FAILED']),
    customFields: unknownRecordSchema.nullable(),
    product: z.object({
      uniqid: z.string(),
      slug: nullableStringSchema,
      serviceText: nullableStringSchema,
      attachment_imageId: z.object({
        cloudflareImageId: nullableStringSchema,
      }).nullable(),
    }).nullable(),
  })),
  currentPayment: customerPortalPaymentSchema.nullable(),
  payments: z.array(customerPortalPaymentSchema),
});

export const customerPortalWalletTopupQuoteSchema = z.object({
  quote: z.object({
    enabled: z.boolean(),
    currency: z.string(),
    min_amount: z.string(),
    max_amount: nullableStringSchema,
    bonus_tiers: z.array(z.object({
      min_amount: z.string(),
      bonus_percent: z.number(),
    })),
  }),
});

export const customerPortalWalletTopupResultSchema = z.object({
  invoice_id: z.string(),
  url: z.string(),
});

export const customerPortalRewardActivityItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.string(),
  amount: nullableStringSchema,
  currency: nullableStringSchema,
  coupon_code: nullableStringSchema,
  reason: z.string(),
  trigger_reference_type: z.string(),
  trigger_reference_id: z.string(),
  created_at: z.number(),
  fulfilled_at: z.number().nullable(),
  expires_at: z.number().nullable(),
});

export const customerPortalRewardsSchema = z.object({
  summary: z.object({
    available: z.string(),
    pending: z.string(),
    lifetime_earned: z.string(),
    redeemed: nullableStringSchema,
    currency: z.string(),
  }),
  activity: z.array(customerPortalRewardActivityItemSchema),
  earned_after_invoice: z.array(customerPortalRewardActivityItemSchema),
  pending_after_invoice: z.array(customerPortalRewardActivityItemSchema),
});

const customerPortalAffiliateLinkSchema = z.object({
  id: z.string(),
  uniqid: z.string(),
  label: nullableStringSchema,
  code: z.string(),
  url: nullableStringSchema,
  status: z.enum(['pending', 'active', 'inactive', 'rejected']),
  is_primary: z.boolean(),
  total_clicks: z.number(),
  total_sales: z.number(),
  total_revenue: z.string(),
  is_active: z.boolean(),
  is_approved: z.boolean(),
  requested_affiliate_revenue_percent: z.number().nullable(),
  requested_affiliate_customer_discount_percent: z.number().nullable(),
  affiliate_revenue_percent_override: z.number().nullable(),
  affiliate_customer_discount_percent_override: z.number().nullable(),
  effective_commission_percent: z.number(),
  effective_customer_discount_percent: z.number(),
  commission_shareback_percent: z.number(),
  commission_shareback_enabled: z.boolean(),
  max_commission_shareback_percent: z.number(),
  created_at: nullableStringSchema,
  updated_at: nullableStringSchema,
});

export const customerPortalAffiliateStatsSchema = z.object({
  window_days: z.number(),
  totals: z.object({
    clicks: z.number(),
    add_to_carts: z.number(),
    checkouts_started: z.number(),
    sales: z.number(),
  }),
  per_link: z.array(z.object({
    link_id: z.string(),
    code: z.string(),
    label: nullableStringSchema,
    clicks: z.number(),
    add_to_carts: z.number(),
    checkouts_started: z.number(),
  })),
  daily: z.array(z.object({
    date: z.string(),
    clicks: z.number(),
    add_to_carts: z.number(),
    checkouts_started: z.number(),
  })),
});

export const customerPortalAffiliateSummarySchema = z.object({
  customer: z.object({
    id: z.string(),
    email: z.string(),
    name: nullableStringSchema,
  }).nullable(),
  state: z.string(),
  can_create_custom_links: z.boolean(),
  custom_link_request_disabled_reason: nullableStringSchema,
  custom_links_require_approval: z.boolean(),
  affiliate_codes_accepted_at_checkout: z.boolean(),
  cash_payout_enabled: z.boolean(),
  links: z.array(customerPortalAffiliateLinkSchema),
  preferred_payout: z.object({
    asset: z.string(),
    network: z.string(),
    address: z.string(),
  }).nullable(),
  balance_currency: z.string(),
  balances: z.object({
    pending: z.string(),
    available: z.string(),
    requested: z.string(),
    converted: z.string(),
    paid: z.string(),
    approved_total: z.string(),
  }),
  commissions: z.array(z.object({
    id: z.string(),
    invoice_id: z.string(),
    commission_amount: z.string(),
    base_amount: z.string(),
    discount_amount: z.string(),
    status: z.string(),
    created_at: nullableStringSchema,
    paid_at: nullableStringSchema,
    converted_at: nullableStringSchema,
  })),
  claims: z.array(z.object({
    id: z.string(),
    type: z.string(),
    status: z.string(),
    amount: z.string(),
    currency: z.string(),
    allocations: z.array(z.object({
      source_type: z.string(),
      commission_id: nullableStringSchema,
      adjustment_id: nullableStringSchema,
      amount: z.string(),
    })),
    wallet_transaction_id: nullableStringSchema,
    payout_asset: nullableStringSchema,
    payout_network: nullableStringSchema,
    payout_address: nullableStringSchema,
    note: nullableStringSchema,
    metadata: z.unknown(),
    completed_at: nullableStringSchema,
    rejected_at: nullableStringSchema,
    cancelled_at: nullableStringSchema,
    created_at: nullableStringSchema,
    updated_at: nullableStringSchema,
  })),
});

export const customerPortalSubscriptionSummarySchema = z.object({
  uniqid: z.string(),
  status: z.string(),
  gateway: z.string(),
  current_period_start: z.string(),
  current_period_end: z.string(),
  trial_end: nullableStringSchema,
  cancel_at_period_end: z.boolean(),
  canceled_at: nullableStringSchema,
  created_at: z.string(),
  plan: z.object({
    id: z.string(),
    uniqid: z.string(),
    name: z.string(),
    price: z.string(),
    currency: z.string(),
    interval: z.string(),
    interval_count: z.number(),
    trial_days: z.number(),
  }).nullable(),
  payment_method: z.object({
    id: z.string(),
    gateway: z.string(),
    type: z.string(),
    card_brand: nullableStringSchema,
    card_last4: nullableStringSchema,
    card_exp_month: z.number().nullable(),
    card_exp_year: z.number().nullable(),
    is_default: z.boolean(),
  }).nullable(),
  actions: z.object({
    can_pause: z.boolean(),
    can_resume: z.boolean(),
    can_cancel: z.boolean(),
    can_manage_payment_method: z.boolean(),
  }),
});

export const customerLoyaltySchema = z.object({
  enabled: z.boolean(),
  account: z.object({
    points_balance: z.number(),
    lifetime_points: z.number(),
  }).nullable(),
  settings: z.object({
    earn_rate_per_usd: z.number(),
    redeem_value_per_point: z.number(),
    min_redeem_points: z.number(),
  }).nullable(),
  transactions: z.array(z.object({
    type: z.enum(['EARN_ORDER', 'EARN_REVIEW', 'REDEEM', 'ADJUST']),
    points: z.number(),
    balance_after: z.number(),
    description: nullableStringSchema,
    created_at: z.string(),
  })),
});

export const customerLoyaltyRedeemSchema = z.object({
  redeemed_points: z.number(),
  wallet_amount: z.string(),
  currency: z.string(),
  points_balance: z.number(),
  replayed: z.boolean(),
});

const customerWarrantySchema = z.object({
  uniqid: z.string(),
  invoice_uniqid: z.string(),
  product_title: z.string(),
  period_days: z.number(),
  starts_at: z.string(),
  expires_at: z.string(),
  status: z.enum(['ACTIVE', 'EXPIRED', 'VOIDED']),
  claim_status: z.enum(['REQUESTED', 'APPROVED', 'DENIED']).nullable(),
  created_at: z.string(),
});

export const customerWarrantyListSchema = z.object({
  warranties: z.array(customerWarrantySchema),
});

export const customerWarrantyClaimSchema = z.object({
  warranty: customerWarrantySchema.extend({
    claim_status: z.literal('REQUESTED'),
  }),
});

// ---------------------------------------------------------------------------
// Headless customer-portal action contracts
// ---------------------------------------------------------------------------

export const customerPortalProfileUpdateSchema = z.object({
  customer: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    joined_at: nullableStringSchema,
  }),
});

export const customerPortalAvatarSchema = z.object({
  avatar_url: nullableStringSchema,
});

export const customerPortalEmailPreferencesSchema = z.object({
  global_unsubscribed: z.boolean(),
  lists: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: nullableStringSchema,
    subscribed: z.boolean(),
  })),
});

export const customerPortalEmailPreferencesUpdateSchema = z.object({
  updated: z.boolean(),
});

export const customerPortalNotificationPreferencesSchema = z.object({
  order_created_emails: z.boolean(),
  order_completed_emails: z.boolean(),
  payment_issue_emails: z.boolean(),
});

export const customerPortalFavoritesSchema = z.object({
  favorites: z.array(z.object({
    product_uniqid: z.string(),
    title: z.string(),
    slug: nullableStringSchema,
    image_url: nullableStringSchema,
    price_display: z.number(),
    unavailable: z.boolean(),
    created_at: z.string(),
  })),
});

export const customerPortalFavoriteMutationSchema = z.union([
  z.object({ added: z.literal(true) }),
  z.object({ removed: z.literal(true) }),
]);

export const customerPortalLicenseResetSchema = z.object({
  license: z.object({
    uniqid: z.string(),
    hardware_id: nullableStringSchema,
  }),
});

export const customerPortalLicenseRenewalSchema = z.object({
  invoice_id: z.string(),
  checkout_url: z.string(),
  preferred_gateways: z.array(z.string()),
  license: z.object({
    uniqid: z.string(),
    product_title: z.string(),
  }),
});

export const customerPortalSubscriptionBillingHistorySchema = z.object({
  invoices: z.array(z.object({
    id: z.string(),
    uniqid: z.string(),
    total: z.string(),
    currency: z.string(),
    status: z.string(),
    gateway: z.string(),
    created_at: z.string(),
    completed_at: z.string(),
  })),
});

export const customerPortalSubscriptionCancelSchema = z.object({
  subscription: z.object({
    id: z.string(),
    status: z.string(),
    cancel_at_period_end: z.boolean(),
    cancel_reason: nullableStringSchema,
    canceled_at: nullableStringSchema,
  }),
});

export const customerPortalSubscriptionStatusSchema = z.object({
  subscription: z.object({
    id: z.string(),
    status: z.string(),
  }),
});

export const customerPortalSupportTicketDetailSchema = z.object({
  ticket: z.object({
    uniqid: z.string(),
    title: z.string(),
    status: z.string(),
    invoice_id: nullableStringSchema,
    created_at: z.string(),
    updated_at: z.string(),
    messages: z.array(z.object({
      id: z.string(),
      role: z.enum(['CUSTOMER', 'SHOP', 'SYSTEM']),
      message: z.string(),
      created_at: z.string(),
    })),
  }),
});

export const customerPortalSupportCreateSchema = z.object({
  uniqid: z.string(),
});

export const customerPortalAffiliateCreateLinkSchema = z.object({
  affiliate_link: z.object({
    id: z.string(),
    customer_id: z.string(),
    label: nullableStringSchema,
    code: z.string(),
    url: nullableStringSchema,
    status: z.enum(['pending', 'approved', 'rejected']),
    requested_affiliate_revenue_percent: z.number().nullable(),
    requested_affiliate_customer_discount_percent: z.number().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  }),
});

const customerReplacementEligibilityCheckSchema = z.object({
  check: z.string(),
  passed: z.boolean(),
  reason: nullableStringSchema.optional(),
});

export const customerReplacementEligibilitySchema = z.object({
  eligible: z.boolean(),
  decision: z.enum(['ELIGIBLE', 'DENIED', 'NO_RULE']),
  reason: nullableStringSchema,
  eligibility_checks: z.array(customerReplacementEligibilityCheckSchema),
});

export const customerReplacementRequestSchema = z.object({
  request_id: z.string(),
  event_id: nullableStringSchema,
  decision: z.enum([
    'ISSUED_REPLACEMENT',
    'FALLBACK_REFUND',
    'FALLBACK_CREDIT',
    'QUEUED_MANUAL',
    'DENIED',
    'EXHAUSTED',
    'FAILED',
  ]),
  status_label: z.enum([
    'ISSUED',
    'REFUNDED',
    'CREDITED',
    'QUEUED_MANUAL',
    'DENIED',
    'EXHAUSTED',
    'FAILED',
    'PENDING',
  ]),
  reason: nullableStringSchema,
  eligibility_checks: z.array(customerReplacementEligibilityCheckSchema),
});

const customerResellerTierSchema = z.object({
  id: z.string(),
  name: nullableStringSchema,
  discount_percent: z.number().nullable(),
});

const customerResellerRelationshipSchema = z.object({
  id: z.string(),
  status: z.enum(['INVITED', 'APPLIED', 'ACTIVE', 'SUSPENDED', 'REJECTED']),
  tier: customerResellerTierSchema.nullable(),
  application_note: nullableStringSchema,
  lifetime_revenue: z.string(),
  lifetime_orders: z.number(),
  approved_at: nullableStringSchema,
  created_at: z.string(),
});

export const customerResellerSummarySchema = z.object({
  program_enabled: z.boolean(),
  api_enabled: z.boolean(),
  portal_purchasing_enabled: z.boolean(),
  enrollment_mode: z.enum(['MANUAL', 'APPLICATION', 'OPEN']),
  application_message: nullableStringSchema,
  require_application_note: z.boolean(),
  reseller: customerResellerRelationshipSchema.nullable(),
});

export const customerResellerEnrollmentSchema = z.object({
  reseller: customerResellerRelationshipSchema,
});

const customerResellerAppliedOverrideSchema = z.enum(['variant', 'product', 'tier']);

const customerResellerCatalogVariantSchema = z.object({
  variant_id: z.string(),
  title: z.string(),
  stock: z.number(),
  supplier_backed: z.boolean(),
  orderable: z.boolean(),
  base_price: z.string(),
  unit_price: z.string(),
  discount_percent: z.number(),
  applied_override: customerResellerAppliedOverrideSchema,
});

export const customerResellerCatalogSchema = z.object({
  tier: customerResellerTierSchema,
  catalog: z.object({
    currency: z.string(),
    total: z.number(),
    page: z.number(),
    per_page: z.number(),
    items: z.array(z.object({
      product_id: z.string(),
      uniqid: z.string(),
      title: z.string(),
      type: z.string(),
      stock: z.number(),
      supplier_backed: z.boolean(),
      orderable: z.boolean(),
      volume_discount_enabled: z.boolean(),
      base_price: z.string(),
      unit_price: z.string(),
      discount_percent: z.number(),
      applied_override: customerResellerAppliedOverrideSchema,
      variants: z.array(customerResellerCatalogVariantSchema),
    })),
  }),
});

export const customerResellerQuoteSchema = z.object({
  quote: z.object({
    currency: z.string(),
    tier: customerResellerTierSchema,
    items: z.array(z.object({
      product_id: z.string(),
      product_title: z.string(),
      product_type: z.string(),
      variant_id: nullableStringSchema,
      variant_title: nullableStringSchema,
      quantity: z.number(),
      base_price: z.string(),
      discount_percent: z.number(),
      unit_price: z.string(),
      line_total: z.string(),
      applied_override: customerResellerAppliedOverrideSchema,
      volume_discount_applied: z.string(),
    })),
    subtotal: z.string(),
    volume_discount_total: z.string(),
    total: z.string(),
    savings: z.string(),
  }),
});

export const customerResellerOrdersSchema = z.object({
  orders: z.object({
    total: z.number(),
    page: z.number(),
    per_page: z.number(),
    items: z.array(z.object({
      uniqid: z.string(),
      status: z.string(),
      currency: z.string(),
      total: z.string(),
      item_count: z.number(),
      created_at: z.string(),
    })),
  }),
});

export const customerResellerOrderSchema = z.object({
  order: z.object({
    uniqid: z.string(),
    status: z.string(),
    currency: z.string(),
    total: z.string(),
    item_count: z.number(),
    created_at: z.string(),
    tier_name: z.string(),
    tier_discount_percent: z.number(),
    partially_delivered: z.boolean(),
    refunded_amount: nullableStringSchema,
    lines: z.array(z.object({
      product_id: nullableStringSchema,
      product_title: z.string(),
      variant_id: nullableStringSchema,
      variant_title: nullableStringSchema,
      quantity: z.number(),
      unit_price: z.string(),
      total: z.string(),
      delivered: z.boolean(),
      delivered_at: nullableStringSchema,
      serials: z.array(z.string()),
    })),
  }),
});

export const customerResellerWalletSchema = z.object({
  wallet: z.object({
    currency: z.string(),
    balance: z.string(),
    hold_balance: z.string(),
    available: z.string(),
    is_active: z.boolean(),
  }),
});

export const customerResellerApiKeysSchema = z.object({
  api_keys: z.array(z.object({
    id: z.string(),
    name: z.string(),
    key_last4: z.string(),
    last_used_at: nullableStringSchema,
    revoked_at: nullableStringSchema,
    created_at: z.string(),
  })),
});

export const customerResellerTopupSchema = z.object({
  topup: z.object({
    uniqid: z.string(),
    url: z.string(),
  }),
});

export const customerPortalDashboardSchema = z.object({
  shop: z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    currency: z.string(),
    branding: customerPortalBrandingSchema,
  }),
  customer: z.object({
    email: z.string(),
    name: z.string(),
  }),
  stats: z.object({
    invoices_count: z.number(),
    licenses_count: z.number(),
    deliverables_count: z.number(),
    tickets_count: z.number(),
  }),
  wallet: z.object({
    available: z.string(),
    total: z.string(),
    hold: z.string(),
    currency: z.string(),
    enabled: z.boolean(),
  }).nullable(),
  rewards: customerPortalRewardsSchema,
  affiliate: customerPortalAffiliateSummarySchema,
  invoices: z.array(customerPortalDashboardInvoiceSchema),
  subscriptions: z.array(customerPortalSubscriptionSummarySchema),
  licenses: z.array(z.object({
    uniqid: z.string(),
    /*
     * Both of these are produced by `getCustomerProductsLicenses` and were
     * simply missing here. Without `invoice_id` a consumer cannot tell which
     * purchase a license belongs to, and without `status` it cannot tell an
     * active license from a suspended, revoked or expired one — the projection
     * computes exactly that.
     */
    invoice_id: z.string(),
    status: z.string(),
    license_key: z.string(),
    hardware_id: nullableStringSchema,
    expires_at: z.number().nullable(),
    /*
     * Server-decided: the product behind this license still exists, still
     * sells through storefront checkout, and still has licensing enabled.
     * The portal cannot derive any of that, so it renders the renew action
     * from this flag alone instead of guessing and offering a dead button.
     */
    renewable: z.boolean(),
    product: z.object({
      title: z.string().optional(),
    }).optional(),
  })),
  deliverables: z.array(z.object({
    id: z.string(),
    invoice_uniqid: z.string(),
    product_title: z.string(),
    kind: z.enum(['file', 'serial', 'license']),
    label: z.string(),
    value: z.string(),
    href: nullableStringSchema,
    created_at: z.string(),
  })),
  tickets: z.array(z.object({
    uniqid: z.string(),
    title: z.string(),
    status: z.string(),
    updated_at: z.string(),
    created_at: nullableStringSchema,
  })),
});

export type CustomerPortalDashboardWire = z.infer<typeof customerPortalDashboardSchema>;
export type CustomerPortalMeWire = z.infer<typeof customerPortalMeSchema>;
export type CustomerPortalAffiliateSummaryWire = z.infer<typeof customerPortalAffiliateSummarySchema>;
export type CustomerPortalAffiliateStatsWire = z.infer<typeof customerPortalAffiliateStatsSchema>;
export type CustomerPortalInvoiceDetailWire = z.infer<typeof customerPortalInvoiceDetailSchema>;
export type CustomerPortalPaginatedInvoicesWire = z.infer<typeof customerPortalPaginatedInvoicesSchema>;
export type CustomerPortalOrderReviewWire = z.infer<typeof customerPortalOrderReviewSchema>;
export type CustomerPortalRewardActivityItemWire = z.infer<typeof customerPortalRewardActivityItemSchema>;
export type CustomerPortalRewardsWire = z.infer<typeof customerPortalRewardsSchema>;
export type CustomerPortalSubscriptionSummaryWire = z.infer<typeof customerPortalSubscriptionSummarySchema>;
export type CustomerPortalWalletTopupQuoteWire = z.infer<typeof customerPortalWalletTopupQuoteSchema>;
export type CustomerPortalWalletTopupResultWire = z.infer<typeof customerPortalWalletTopupResultSchema>;
export type CustomerLoyaltyWire = z.infer<typeof customerLoyaltySchema>;
export type CustomerLoyaltyRedeemWire = z.infer<typeof customerLoyaltyRedeemSchema>;
export type CustomerWarrantyListWire = z.infer<typeof customerWarrantyListSchema>;
export type CustomerWarrantyClaimWire = z.infer<typeof customerWarrantyClaimSchema>;
export type CustomerPortalProfileUpdateWire = z.infer<typeof customerPortalProfileUpdateSchema>;
export type CustomerPortalAvatarWire = z.infer<typeof customerPortalAvatarSchema>;
export type CustomerPortalEmailPreferencesWire = z.infer<typeof customerPortalEmailPreferencesSchema>;
export type CustomerPortalEmailPreferencesUpdateWire = z.infer<typeof customerPortalEmailPreferencesUpdateSchema>;
export type CustomerPortalNotificationPreferencesWire = z.infer<typeof customerPortalNotificationPreferencesSchema>;
export type CustomerPortalFavoritesWire = z.infer<typeof customerPortalFavoritesSchema>;
export type CustomerPortalFavoriteMutationWire = z.infer<typeof customerPortalFavoriteMutationSchema>;
export type CustomerPortalLicenseResetWire = z.infer<typeof customerPortalLicenseResetSchema>;
export type CustomerPortalLicenseRenewalWire = z.infer<typeof customerPortalLicenseRenewalSchema>;
export type CustomerPortalSubscriptionBillingHistoryWire = z.infer<typeof customerPortalSubscriptionBillingHistorySchema>;
export type CustomerPortalSubscriptionCancelWire = z.infer<typeof customerPortalSubscriptionCancelSchema>;
export type CustomerPortalSubscriptionStatusWire = z.infer<typeof customerPortalSubscriptionStatusSchema>;
export type CustomerPortalSupportTicketDetailWire = z.infer<typeof customerPortalSupportTicketDetailSchema>;
export type CustomerPortalSupportCreateWire = z.infer<typeof customerPortalSupportCreateSchema>;
export type CustomerPortalAffiliateCreateLinkWire = z.infer<typeof customerPortalAffiliateCreateLinkSchema>;
export type CustomerReplacementEligibilityWire = z.infer<typeof customerReplacementEligibilitySchema>;
export type CustomerReplacementRequestWire = z.infer<typeof customerReplacementRequestSchema>;
export type CustomerResellerSummaryWire = z.infer<typeof customerResellerSummarySchema>;
export type CustomerResellerEnrollmentWire = z.infer<typeof customerResellerEnrollmentSchema>;
export type CustomerResellerCatalogWire = z.infer<typeof customerResellerCatalogSchema>;
export type CustomerResellerQuoteWire = z.infer<typeof customerResellerQuoteSchema>;
export type CustomerResellerOrdersWire = z.infer<typeof customerResellerOrdersSchema>;
export type CustomerResellerOrderWire = z.infer<typeof customerResellerOrderSchema>;
export type CustomerResellerWalletWire = z.infer<typeof customerResellerWalletSchema>;
export type CustomerResellerApiKeysWire = z.infer<typeof customerResellerApiKeysSchema>;
export type CustomerResellerTopupWire = z.infer<typeof customerResellerTopupSchema>;
